import { ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';

/**
 * useTruncated — "este texto está cortado na tela AGORA?", pra mostrar um
 * tooltip SÓ nesse caso.
 *
 * Os dois extremos são ruins: tooltip fixo repetindo um texto inteiramente
 * visível é ruído (e ensina a ignorar tooltip), e texto cortado sem tooltip é
 * dado sumindo em silêncio.
 *
 * Cobre os dois cortes do CSS: `truncate` (uma linha, corte horizontal) e
 * `line-clamp-N` (corte vertical). O +1 nas comparações absorve arredondamento
 * sub-pixel do layout — sem a folga, uma linha que cabe exatamente acusaria
 * corte e ganharia tooltip à toa.
 *
 * Dois modos, MESMA medição:
 *
 * 1. SOB DEMANDA (default) — a checagem roda no mouseenter (barata, sob
 *    demanda; não paga ResizeObserver por nó, o que importa em listas
 *    grandes/virtualizadas):
 *
 *      const { targetRef, isTruncated, checkTruncation } = useTruncated();
 *      <div ref="targetRef" class="truncate"
 *           v-tooltip="{ content: label, disabled: !isTruncated }"
 *           @mouseenter="checkTruncation">{{ label }}</div>
 *
 * 2. OBSERVADO (`observe: true`) — mede no mount, remede quando o elemento
 *    muda de tamanho (ResizeObserver) e quando o conteúdo muda (`source`).
 *    Pra quando o veredito precisa estar certo ANTES do hover (ex.:
 *    v-tooltip com content condicionado ao corte):
 *
 *      const { targetRef, isTruncated } = useTruncated({
 *        observe: true,
 *        source: () => props.name,
 *      });
 *
 * @param {object} [options]
 * @param {boolean} [options.observe] mede no mount + reage a resize
 * @param {Function} [options.source] getter do texto — remede quando mudar
 */
export function useTruncated({ observe = false, source = null } = {}) {
  const targetRef = ref(null);
  const isTruncated = ref(false);

  const checkTruncation = () => {
    const el = targetRef.value;
    if (!el) {
      // Nada medido, nada afirmado.
      isTruncated.value = false;
      return;
    }
    isTruncated.value =
      el.scrollWidth > el.clientWidth + 1 ||
      el.scrollHeight > el.clientHeight + 1;
  };

  if (observe) {
    let observer = null;
    onMounted(() => {
      checkTruncation();
      // jsdom (specs) e navegadores antigos não têm ResizeObserver: sem ele a
      // medição inicial vale e o tooltip só não reage a resize. Degrada pra
      // "menos tooltip", nunca pra tooltip errado.
      if (typeof ResizeObserver === 'undefined' || !targetRef.value) return;
      observer = new ResizeObserver(checkTruncation);
      observer.observe(targetRef.value);
    });
    onBeforeUnmount(() => {
      observer?.disconnect();
      observer = null;
    });
  }

  if (source) watch(source, () => nextTick(checkTruncation));

  return { targetRef, isTruncated, checkTruncation };
}
